<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { defineProps, defineExpose, ref, computed } from 'vue';

/*==================================================================*/
/*===================== IMPORTS UTILS ==============================*/
/*==================================================================*/

import UtilObject from '@/utils/UtilObject';
import UtilDateTime from '@/utils/UtilDateTime';
import UtilArray from '@/utils/UtilArray.mjs';
import UtilURL from '@/utils/UtilURL';

/*==================================================================*/
/*================ IMPORTS COMPONENTES =============================*/
/*==================================================================*/

import ModalFullscreen from '@/components/v2/modais/ModalFullscreen/ModalFullscreen.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import ConteudoModal from './ConteudoModal.vue';

/*==================================================================*/
/*================ IMPORTS SERVICES ================================*/
/*==================================================================*/

import OrdemDePagamentoAprovadoresService from '@/api/v2/services/OrdemDePagamentoAprovadoresService';

/*==================================================================*/
/*================== STORE DE COLABORADOR ==========================*/
/*==================================================================*/

import useColaboradorStore from '@/api/v2/stores/ColaboradorStore.js';
const colaboradorStore = useColaboradorStore();

//==================================================================//
//===================== ADMIN ID & OWNER ===========================//
//==================================================================//

const ADMIN_OWNER = "23e308ed-4f67-4394-9a77-7228d2992dc3";
const ADMIN_ID = "9a405812-b83c-4b9f-8ad3-68c91a33725a";

/*==================================================================*/
/*================== PROPS =========================================*/
/*==================================================================*/

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false
    },
    onClose: {
        type: Function,
        default: null
    }
});

/*==================================================================*/
/*================== MODAL FULLSCREEN REF ==========================*/
/*==================================================================*/

const modalFullscreenRef = ref();

/*==================================================================*/
/*================== DADO ==========================================*/
/*==================================================================*/

const dado = ref({});

/*==================================================================*/
/*================== APROVACAO =====================================*/
/*==================================================================*/

const aprovacao = ref({});

/*==================================================================*/
/*================== PAGAMENTO =====================================*/
/*==================================================================*/

const pagamento = computed(() => {

    return dado?.value?.parcelas?.[0] || {};

});

/*==================================================================*/
/*================== APROVACAO TIPO ================================*/
/*==================================================================*/

const aprovacaoTipo = computed(() => {

    let status = dado?.value?.status || "";
    let aprovacaoTipo = "Nenhum";

    switch(status){
        case "Aguardando Requisitante":
            aprovacaoTipo = "Requisitante";
        break;
        case "Aguardando Coordenador":
            aprovacaoTipo = "Coordenador";
        break;
        case "Aguardando Gestor":
            aprovacaoTipo = "Gestor";
        break;
        case "Aguardando Financeiro":
            aprovacaoTipo = "Financeiro";
        break;
        default:
            aprovacaoTipo = "Nenhum";
    }

    return aprovacaoTipo;

});

/*==================================================================*/
/*================== APROVADOR IS ADMIN ============================*/
/*==================================================================*/

const aprovadorIsAdmin = computed(() => {

    let colaboradorID = colaboradorStore?.colaborador?.id || "";

    return colaboradorID === ADMIN_ID;

});

/*==================================================================*/
/*=================== APROVACOES REQUISITANTES =====================*/
/*==================================================================*/

const aprovacoesRequisitantes = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === "Requisitante";

    });

});

/*==================================================================*/
/*=================-= APROVACOES COORDENADORES =====================*/
/*==================================================================*/

const aprovacoesCoordenadores = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === "Coordenador";

    });

});

/*==================================================================*/
/*=================== APROVACOES GESTORES ==========================*/
/*==================================================================*/

const aprovacoesGestores = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === "Gestor";

    });

});

/*==================================================================*/
/*=================== APROVACOES FINANCEIROS =======================*/
/*==================================================================*/

const aprovacoesFinanceiros = computed(() => {

    return dado?.value?.aprovadores?.filter((aprovador = {}) => {

        return aprovador?.aprovadorTipo === "Financeiro";

    });

});

/*==================================================================*/
/*=================== APROVACOES DO TIPO ===========================*/
/*==================================================================*/

const aprovacoesDoTipo = computed(() => {

    let tipo = aprovacaoTipo?.value || "Nenhum";

    if(tipo === "Requisitante"){
        return aprovacoesRequisitantes?.value || [];
    }

    if(tipo === "Coordenador"){
        return aprovacoesCoordenadores?.value || [];
    }

    if(tipo === "Gestor"){
        return aprovacoesGestores?.value || [];
    }

    if(tipo === "Financeiro"){
        return aprovacoesFinanceiros?.value || [];
    }

    return [];

});

/*==================================================================*/
/*================== APROVADORES PARA EXIBICAO =====================*/
/*==================================================================*/

const aprovadoresParaExibicao = computed(() => {

    let tipoAtual = aprovacaoTipo?.value ?? 'Nenhum';

    if(tipoAtual === 'Requisitante'){

        return aprovacoesRequisitantes?.value;

    }

    if(tipoAtual === 'Coordenador'){

        return UtilArray.concat(
            aprovacoesRequisitantes?.value,
            aprovacoesCoordenadores?.value
        );

    }

    if(tipoAtual === 'Gestor'){

        return UtilArray.concat(
            aprovacoesRequisitantes?.value,
            aprovacoesCoordenadores?.value,
            aprovacoesGestores?.value
        );

    }

    if(tipoAtual === 'Financeiro'){

        return UtilArray.concat(
            aprovacoesRequisitantes?.value,
            aprovacoesCoordenadores?.value,
            aprovacoesGestores?.value,
            aprovacoesFinanceiros?.value
        );

    }

    return dado?.value?.aprovadores ?? [];

});

/*==================================================================*/
/*================== DISABLED APROVAR ORDEM DE PAGAMENTO ===========*/
/*==================================================================*/

const disabledAprovarOrdemDePagamento = computed(() => {

    // Se estiver Disabled.
    if(props.disabled){
        return true;
    }

    // Se não tiver um Tipo de Aprovador.
    if(!aprovacao?.value?.aprovadorTipo){
        return true;
    }

    // Se não tiver um Aprovador.
    if(!aprovacao?.value?.aprovador?.id){
        return true;
    }

    // Se tiver uma Justificativa preenchida.
    if(aprovacao?.value?.justificativa){
        return true;
    }

    // Se não tiver uma Data de Decisão.
    if(!aprovacao?.value?.dataDecisao){
        return true;
    }

    return false;

});

/*==================================================================*/
/*================== DISABLED REPROVAR ORDEM DE PAGAMENTO ==========*/
/*==================================================================*/

const disabledReprovarOrdemDePagamento = computed(() => {

    // Se estiver Disabled.
    if(props.disabled){
        return true;
    }

    // Se não tiver um Tipo de Aprovador.
    if(!aprovacao?.value?.aprovadorTipo){
        return true;
    }

    // Se não tiver um Aprovador.
    if(!aprovacao?.value?.aprovador?.id){
        return true;
    }

    // Se não tiver uma Justificativa preenchida.
    if(!aprovacao?.value?.justificativa){
        return true;
    }

    // Se não tiver uma Data de Decisão.
    if(!aprovacao?.value?.dataDecisao){
        return true;
    }

    return false;

});

/*==================================================================*/
/*=================== SELECIONA APROVACAO ==========================*/
/*==================================================================*/

const selecionaAprovacao = (aprovacaoSelecionada = {}) => {

    aprovacaoSelecionada = UtilObject.clone(aprovacaoSelecionada);
    aprovacaoSelecionada.dataDecisao = UtilDateTime.todayUTC();

    aprovacao.value = aprovacaoSelecionada;

};

/*==================================================================*/
/*================== ALTERA APROVACAO ==============================*/
/*==================================================================*/

const alteraAprovacao = (update = {}) => {

    aprovacao.value = UtilObject.merge(aprovacao.value, update);

};

/*==================================================================*/
/*================== PREPARA APROVADORES ===========================*/
/*==================================================================*/

const preparaAprovadores = (aprovadores = []) => {

    return aprovadores.map((aprovador = {}) => ({

        id: aprovador.id ?? '',
        codigo: aprovador.codigo ?? '',
        status: aprovador.status ?? 'Valido',
        aprovadorTipo: aprovador.aprovadorTipo ?? 'Nenhum',
        aprovador: aprovador.aprovador ?? {},
        decisao: aprovador.decisao ?? 'Nula',
        justificativa: aprovador.justificativa ?? '',
        dataDecisao: aprovador.dataDecisao ?? '',

    }));

};

/*==================================================================*/
/*================== PREPARA DESPESAS ==============================*/
/*==================================================================*/

const preparaDespesas = async (despesas = []) => {

    let despesasAUX = [];

    for(const despesa of despesas ?? []){

        let despesaAUX = {
            id: despesa.id ?? '',
            numero: despesa.numero ?? '',
            despesa: despesa.despesa ?? {},
            dataDaDespesa: despesa.dataDaDespesa ?? '',
            dataCriacao: despesa.dataCriacao ?? '',
            valor: despesa.valor ?? 0,
            veiculoTipo: despesa.veiculoTipo ?? '',
            veiculoPlaca: despesa.veiculoPlaca ?? '',
            veiculoKMInicial: despesa.veiculoKMInicial ?? 0,
            veiculoKMFinal: despesa.veiculoKMFinal ?? 0,
            observacao: despesa.observacao ?? '',
            anexos: await UtilURL.manyToFile(despesa?.anexos || []),
        }

        despesasAUX.push(despesaAUX);

    }

    return despesasAUX;

};

/*==================================================================*/
/*================== PREPARA PARCELAS ==============================*/
/*==================================================================*/

const preparaParcelas = (parcelas = []) => {

    return parcelas.map((parcela = {}) => ({
        id: parcela.id ?? '',
        numero: parcela.numero || '',
        descricao: parcela.descricao ?? '',
        dataCriacao: parcela.dataCriacao ?? '',
        dataDoPagamento: parcela.dataDoPagamento ?? '',
        valor: parcela.valor ?? 0,
    }));

};

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = async (dadoParaPreparar = {}) => {

    return{
        id: dadoParaPreparar.id ?? '',
        numero: dadoParaPreparar.numero ?? '',
        status: dadoParaPreparar.status ?? '',
        emitente: dadoParaPreparar.emitente ?? {},
        requisitante: dadoParaPreparar.requisitante ?? {},
        tipo: dadoParaPreparar.tipo ?? '',
        notaFiscal: dadoParaPreparar.notaFiscal ?? '',
        tipoCredor: dadoParaPreparar.tipoCredor ?? '',
        credorColaborador: dadoParaPreparar.credorColaborador ?? {},
        credorFornecedor: dadoParaPreparar.credorFornecedor ?? {},
        formaDePagamento: dadoParaPreparar.formaDePagamento ?? '',
        recorrencia: dadoParaPreparar.recorrencia ?? false,
        recorrenciaQuantidadeTotal: dadoParaPreparar.recorrenciaQuantidadeTotal ?? 0,
        recorrenciaQuantidadeAtual: dadoParaPreparar.recorrenciaQuantidadeAtual ?? 0,
        dataCriacao: dadoParaPreparar.dataCriacao ?? '',
        dataPrevisaoPagamento: dadoParaPreparar.dataPrevisaoPagamento ?? '',
        dataAprovacaoPagamento: dadoParaPreparar.dataAprovacaoPagamento ?? '',
        valorTotal: dadoParaPreparar.valorTotal ?? 0,
        justificativa: dadoParaPreparar.justificativa ?? '',
        job: dadoParaPreparar.job ?? {},
        ordemDeServico: dadoParaPreparar.ordemDeServico ?? {},
        owners: dadoParaPreparar.owners ?? [],
        despesas: await preparaDespesas(dadoParaPreparar.despesas ?? []),
        parcelas: preparaParcelas(dadoParaPreparar.parcelas ?? []),
        aprovadores: preparaAprovadores(dadoParaPreparar.aprovadores ?? []),
    };

};

/*==================================================================*/
/*=============== PEGA APROVACAO DO APROVADOR ======================*/
/*==================================================================*/

const pegaAprovacaoDoAprovador = () => {

    /*==================================================================*/
    /*===================== INICIALIZA VALORES =========================*/
    /*==================================================================*/

    let aprovacaoEncontrada = {};
    let colaboradorID = colaboradorStore?.colaborador?.id || "";
    
    /*==================================================================*/
    /*====================== APROVADOR É ADMIN =========================*/
    /*==================================================================*/

    if(aprovadorIsAdmin?.value){

        aprovacaoEncontrada = aprovacoesDoTipo?.value?.[0] || {};
        return aprovacaoEncontrada;

    }

    /*==================================================================*/
    /*================== ENCONTRE O APROVADOR LOGADO ===================*/
    /*==================================================================*/

    aprovacaoEncontrada = aprovacoesDoTipo?.value?.find((aprovacao = {}) => {

        return aprovacao?.aprovador?.id === colaboradorID;

    }) || {};

    return aprovacaoEncontrada;

};

/*==================================================================*/
/*================== PREPARA APROVACAO =============================*/
/*==================================================================*/

const preparaAprovacao = () => {

    let aprovacao = pegaAprovacaoDoAprovador();

    selecionaAprovacao(aprovacao);

};

/*==================================================================*/
/*================== APROVAR ORDEM DE PAGAMENTO ====================*/
/*==================================================================*/

const aprovarOrdemDePagamento = async () => {

    let aprovacaoValores = {
        id: aprovacao?.value?.id || "",
        tipo: aprovacao?.value?.aprovadorTipo || "",
        aprovador: aprovacao?.value?.aprovador?.id || "",
        justificativa: aprovacao?.value?.justificativa || "",
    }

    let response = await OrdemDePagamentoAprovadoresService.executaAprovacao(aprovacaoValores);
    if(response?.status != 200){
        throw new Error('Falha ao aprovar a Ordem de Pagamento');
    }

    // Fechar modal após sucesso
    await fecharModal();

};

/*==================================================================*/
/*================== REPROVAR ORDEM DE PAGAMENTO ===================*/
/*==================================================================*/

const reprovarOrdemDePagamento = async () => {

    let reprovacaoValores = {
        id: aprovacao?.value?.id || "",
        tipo: aprovacao?.value?.aprovadorTipo || "",
        reprovador: aprovacao?.value?.aprovador?.id || "",
        justificativa: aprovacao?.value?.justificativa || "",
    }

    let response = await OrdemDePagamentoAprovadoresService.executaReprovacao(reprovacaoValores);
    if(response?.status != 200){
        throw new Error('Falha ao reprovar a Ordem de Pagamento');
    }

    // Fechar modal após sucesso
    await fecharModal();

};

/*==================================================================*/
/*================== ABRIR MODAL ===================================*/
/*==================================================================*/

const abrirModal = async (dadoParaEditar = {}) => {

    dado.value = await preparaDados(dadoParaEditar);
    preparaAprovacao();

    await modalFullscreenRef.value?.abrirModal();

};

/*==================================================================*/
/*================== FECHAR MODAL ==================================*/
/*==================================================================*/

const fecharModal = async () => {

    await modalFullscreenRef.value?.fecharModal();

};

/*==================================================================*/
/*================== AO FECHAR MODAL ===============================*/
/*==================================================================*/

const aoFecharModal = async () => {

    await props.onClose?.();

};

/*==================================================================*/
/*================== EXPOSE ========================================*/
/*==================================================================*/

defineExpose({ abrirModal, fecharModal });

</script>

<template>

    <ModalFullscreen
        ref="modalFullscreenRef"
        :dado="dado"
        :disabled="props.disabled"
        @onClose="aoFecharModal"
    >

        <template #modal-navegacao-titulo>

            Aprovar Ordem de Pagamento

        </template>

        <template #modal-navegacao-botoes>

            <BotaoTextoBase
                color="#ff5252"
                :disabled="disabledReprovarOrdemDePagamento"
                :click="reprovarOrdemDePagamento"
            >
                Reprovar Ordem de Pagamento
            </BotaoTextoBase>

            <BotaoTextoBase
                color="#4CAF50"
                :disabled="disabledAprovarOrdemDePagamento"
                :click="aprovarOrdemDePagamento"
            >
                Aprovar Ordem de Pagamento
            </BotaoTextoBase>

        </template>

        <template #modal-conteudo>

            <ConteudoModal
                :dado="dado"
                :pagamento="pagamento"
                :aprovacao="aprovacao"
                :aprovacaoTipo="aprovacaoTipo"
                :aprovadoresParaExibicao="aprovadoresParaExibicao"
                :aprovadorIsAdmin="aprovadorIsAdmin"
                :aprovacoesDoTipo="aprovacoesDoTipo"
                :disabled="props.disabled"
                @update:alteraAprovacao="alteraAprovacao"
                @update:selecionaAprovacao="selecionaAprovacao"
            />

        </template>

    </ModalFullscreen>

</template>

<style scoped>
</style>
